import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import ManagePreferencesModal from "./manage-preferences"
import CookiePreferencesIcon from "./cookie-icon"
import { useAuth } from "@/context/Auth"
import axios from "@/utils/axios"

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false)
  const [showIcon, setShowIcon] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const { user } = useAuth()

  // Sync preferences to backend
  const syncToBackend = useCallback(async (consent, preferences) => {
    if (!user) return

    try {
      await axios.put("/auth/cookie-preferences", {
        consent,
        preferences
      })
    } catch (error) {
      console.error("Error syncing cookie preferences to backend:", error)
      // Don't throw - localStorage is already updated
    }
  }, [user])

  // Fetch preferences from backend if user is authenticated
  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true)
      
      try {
        if (user) {
          // User is authenticated - fetch from backend
          try {
            const response = await axios.get("/auth/cookie-preferences")
            if (response.data?.success && response.data?.data) {
              const { consent, preferences } = response.data.data
              
              if (consent || preferences) {
                // Backend has preferences
                if (consent) {
                  localStorage.setItem("cookie_consent", consent)
                }
                if (preferences) {
                  localStorage.setItem("cookie_preferences", JSON.stringify(preferences))
                }
                setShowBanner(false)
                setShowIcon(true)
              } else {
                // No backend preferences, check localStorage
                const localConsent = localStorage.getItem("cookie_consent")
                const localPreferences = localStorage.getItem("cookie_preferences")
                
                if (localConsent || localPreferences) {
                  // Sync localStorage to backend
                  await syncToBackend(localConsent, localPreferences ? JSON.parse(localPreferences) : null)
                  setShowBanner(false)
                  setShowIcon(true)
                } else {
                  setShowBanner(true)
                  setShowIcon(false)
                }
              }
            } else {
              // Fallback to localStorage
              checkLocalStorage()
            }
          } catch (error) {
            // If 401/403, user might not be fully authenticated, use localStorage
            if (error.response?.status === 401 || error.response?.status === 403) {
              checkLocalStorage()
            } else {
              console.error("Error fetching cookie preferences:", error)
              checkLocalStorage()
            }
          }
        } else {
          // User not authenticated - use localStorage only
          checkLocalStorage()
        }
      } catch (error) {
        console.error("Error loading preferences:", error)
        checkLocalStorage()
      } finally {
        setIsLoading(false)
      }
    }

    const checkLocalStorage = () => {
      const consent = localStorage.getItem("cookie_consent")
      const preferences = localStorage.getItem("cookie_preferences")

      if (!consent && !preferences) {
        setShowBanner(true)
        setShowIcon(false)
      } else {
        setShowBanner(false)
        setShowIcon(true)
      }
    }

    loadPreferences()
  }, [user, syncToBackend])

  const handleAcceptAll = async () => {
    const consent = "accepted"
    const allPreferences = { essential: true, analytics: true, marketing: true, functional: true }
    
    localStorage.setItem("cookie_consent", consent)
    localStorage.setItem("cookie_preferences", JSON.stringify(allPreferences))
    setShowBanner(false)
    setShowIcon(true)
    
    // Sync to backend if authenticated
    if (user) {
      await syncToBackend(consent, allPreferences)
    }
    
    // console.log("Cookie consent accepted (all).")
  }

  const handleRejectAll = async () => {
    const consent = "rejected"
    const rejectedPreferences = { essential: true, analytics: false, marketing: false, functional: false }
    
    localStorage.setItem("cookie_consent", consent)
    localStorage.setItem("cookie_preferences", JSON.stringify(rejectedPreferences))
    setShowBanner(false)
    setShowIcon(true)
    
    // Sync to backend if authenticated
    if (user) {
      await syncToBackend(consent, rejectedPreferences)
    }
    
    // console.log("Cookie consent rejected (all).")
  }

  const handleOpenManagePreferences = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = (preferencesSaved = false) => {
    setIsModalOpen(false)
    // If preferences were saved, ensure banner is hidden and icon is shown
    if (preferencesSaved) {
      setShowBanner(false)
      setShowIcon(true)
      localStorage.setItem("cookie_consent", "custom") // Mark as custom consent
    }
  }

  const handleSavePreferences = async (preferences) => {
    const consent = "custom"
    
    localStorage.setItem("cookie_consent", consent)
    localStorage.setItem("cookie_preferences", JSON.stringify(preferences))
    
    // Sync to backend if authenticated
    if (user) {
      await syncToBackend(consent, preferences)
    }
    
    handleCloseModal(true) // Pass true to indicate preferences were saved
    // console.log("Cookie preferences saved:", preferences)
  }

  // Don't render anything while loading to avoid flickering
  if (isLoading) {
    return null
  }

  return (
    <>
      {showBanner && (
        <Card className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t p-4 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex-1 text-sm text-center md:text-left">
            <p>
              We use cookies to improve your experience and for marketing purposes. By continuing to use our site, you
              agree to our{" "}
              <Link href="/privacy-policy" className="underline font-medium">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <Button variant="outline" onClick={handleOpenManagePreferences} className="w-full sm:w-auto bg-transparent">
              Manage Preferences
            </Button>
            <Button variant="destructive" onClick={handleRejectAll} className="w-full sm:w-auto">
              Reject All
            </Button>
            <Button onClick={handleAcceptAll} className="w-full sm:w-auto">
              Accept All
            </Button>
          </div>
        </Card>
      )}

      {showIcon && !isModalOpen && <CookiePreferencesIcon onOpenModal={handleOpenManagePreferences} />}

      <ManagePreferencesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSavePreferences={handleSavePreferences}
      />
    </>
  )
}
